import React, { useState, useEffect } from 'react';
import { Fab, Tooltip, Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  DeleteForever
} from "@mui/icons-material";

import { 
  Feature, 
  FloatingFeature,
} from "@models";
import { 
  AttachedTable,
  CharacterFeatureSource, 
  CharacterSheetInterface, 
  CreatureInstance, 
  SummonInstance 
} from "@dndModels";
import { FEATURE_TYPES } from "@dndModels/Constants";

import FloatingFeatureBox from "@dndComponents/FloatingFeatureBox";
import FloatingFeatureInput from "@dndComponents/feature/FloatingFeatureInput";
import { FeatureType } from "@dndModels/Types";

interface Props {
  label: string;
  parent_name: string;
  base_name?: string | null;
  feature: Feature;
  onChange: (feature: Feature) => void; 
  onDelete: () => void; 
  onDone: (target: string) => void;
  slot_level?: number;
  minion?: boolean;
  disabled?: boolean;
  formHeight?: number;
  allowed_feature_types?: FeatureType[];
  cfs?: CharacterSheetInterface | CreatureInstance | SummonInstance | CharacterFeatureSource;
  attached_table?: AttachedTable;
}

export default function FeatureInput({ 
  label,
  parent_name,
  base_name = null,
  feature,
  onChange,
  onDelete,
  onDone,
  slot_level = -1,
  minion = false,
  disabled = false,
  formHeight,
  allowed_feature_types = FEATURE_TYPES,
  cfs,
  attached_table
}: Props) {
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [my_feature, setFeature] = useState(feature.clone());
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
     
  }, [reloading]);

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 12
        }}>
        <Button
          variant="contained"
          color="primary"
          disabled={ my_feature.name === "" || unsavedChanges }
          onClick={ () => { 
            onDone("parent");
          }}>
          { parent_name === "" ? `Nameless ${my_feature.parent_type}` : parent_name }
        </Button>
        { base_name !== null &&
          <span>
            &gt;
            <Button
              variant="contained"
              color="primary"
              disabled={ my_feature.name === "" || unsavedChanges }
              onClick={ () => { 
                onDone("base");
              }}>
              { base_name === "" ? `Nameless Feature Base` : base_name }
            </Button>
          </span>
        }
      </Grid>
      { !disabled &&
        <>
          <Grid
            size={{
              xs: 2
            }}>
            <Tooltip title={`Delete ${label}`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  onDelete();
                }}>
                <DeleteForever/>
              </Fab>
            </Tooltip>
          </Grid>
          <Grid
            size={{
              xs: 10
            }}>
            <FloatingFeatureBox
              floating_feature_id={my_feature.floating_feature_id}
              allowed_feature_types={allowed_feature_types}
              onFloatingFeatureSelected={(floating_feature: FloatingFeature) => {
                const new_feature = my_feature;
                new_feature.floating_feature_id = floating_feature._id;
                new_feature.floating_feature = floating_feature;
                setFeature(new_feature);
                setReloading(true);
                setUnsavedChanges(false);
                onChange(new_feature);
              }}
            />
          </Grid>
        </>
      }
      { !reloading &&
        <Grid
          size={{
            xs: 12
          }}>
          <FloatingFeatureInput 
            floating_feature={my_feature.floating_feature}
            slot_level={slot_level}
            disabled={disabled}
            allowed_feature_types={allowed_feature_types}
            height={formHeight === undefined ? undefined : formHeight - 149}
            attached_table={attached_table}
            onDelete={() => {
              const new_feature = my_feature;
              new_feature.floating_feature_id = "";
              new_feature.floating_feature = null;
              setFeature(new_feature);
              onChange(new_feature);
            }}
            onAdd={(floating_feature: FloatingFeature) => {
              const new_feature = my_feature;
              new_feature.floating_feature_id = floating_feature._id;
              new_feature.floating_feature = floating_feature;
              setFeature(new_feature);
              onChange(new_feature);
            }}
            unsavedChange={setUnsavedChanges}
            cfs={cfs}
          />
        </Grid>
      }
    </Grid>
  );
}
